import { measureText, truncateToWidth } from '@/explore/utils/text-measurement';

import {
  seriesColor1,
  seriesColor2,
  seriesColor3,
  seriesColor4,
  seriesColor5,
  seriesColor6,
  seriesColor7,
  seriesColor8,
  seriesColor9,
  seriesColor10,
  seriesColor11,
  seriesColor12,
  seriesColor13,
  seriesColor14,
  seriesColor15,
  seriesColor16,
  seriesColor17,
  seriesColor18,
  fontFamily,
} from './charts.module.scss';

export const chartColors = [
  seriesColor1,
  seriesColor2,
  seriesColor3,
  seriesColor4,
  seriesColor5,
  seriesColor6,
  seriesColor7,
  seriesColor8,
  seriesColor9,
  seriesColor10,
  seriesColor11,
  seriesColor12,
  seriesColor13,
  seriesColor14,
  seriesColor15,
  seriesColor16,
  seriesColor17,
  seriesColor18,
];
export const getChartColor = (index: number) => chartColors[index % chartColors.length];

export const increaseBrightness = (hex: string, percent: number) => {
  // strip the leading # if it's there
  hex = hex.replace(/^\s*#|\s*$/g, '');

  // convert 3 char codes --> 6, e.g. `E0F` --> `EE00FF`
  if (hex.length === 3) {
    hex = hex.replace(/(.)/g, '$1$1');
  }

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  const adjustBrightness = (color: number) => {
    const newColor = color + (255 - color) * (percent / 100);
    return Math.round(newColor).toString(16).padStart(2, '0'); // Ensure two digits
  };

  return `#${adjustBrightness(r)}${adjustBrightness(g)}${adjustBrightness(b)}`;
};

export const getScreenPoint = (
  point: { x: number; y: number },
  element: HTMLElement | SVGElement | null,
) => {
  if (element === null) {
    return point;
  }
  // svg
  const svg = (element as SVGElement)?.ownerSVGElement;
  if (svg !== undefined && svg !== null) {
    const screenCTM = svg.getScreenCTM();
    if (screenCTM === null) {
      return point;
    }
    return DOMPoint.fromPoint(point).matrixTransform(screenCTM);
  }
  // html
  const rect = element.getBoundingClientRect();
  return {
    x: rect.left + point.x,
    y: rect.top + point.y,
  };
};

export const sortTooltipData = <
  T extends {
    points: { label: string; value: number | null }[];
  },
>(
  data: T,
) => {
  return {
    ...data,
    points: data.points.toSorted((a, b) => {
      if (a.value === null || b.value === null) {
        return a.value === null ? 1 : -1;
      }

      if (a.value === b.value) {
        return b.label < a.label ? -1 : 1;
      }

      return b.value - a.value;
    }),
  };
};

const LabelFont = `12px ${fontFamily}`;
const LabelLetterSpacing = '1';

/**
 * Calculates the maximum text width from an array of labels
 */
export const getMaxLabelWidth = (labels: string[], font = LabelFont): number => {
  return Math.max(...labels.map((label) => measureText(label, font, LabelLetterSpacing)));
};

/**
 * Truncates a label to fit within a maximum pixel width, adding ellipsis if needed.
 */
export const truncateLabelToWidth = (label: string, maxWidth: number, font = LabelFont): string => {
  return truncateToWidth(label, maxWidth, font, LabelLetterSpacing);
};

/**
 * Sanitizes a string for use in SVG gradient IDs.
 * Only allows alphanumeric characters, hyphens, underscores, and colons.
 * Other characters (spaces, #, parentheses, quotes, etc.) can cause issues
 * when used in url() references.
 */
export const sanitizeGradientId = (id: string): string => {
  return id.replace(/[^a-zA-Z0-9_:-]/g, '_');
};

export type HighlightedData = {
  date: Date;
  group: string | undefined;
  axis: 'left' | 'right' | undefined;
  seriesKey?: string;
  chartType?: 'stacked' | 'grouped';
  point: {
    x: number;
    y: number;
  };
};

export interface SeriesDetectionData {
  key: string;
  value: number | null;
  chartType: 'line' | 'area' | 'bar';
  axis: 'left' | 'right';
}

export interface SeriesDetectionConfig {
  mouseX?: number;
  mouseY: number;
  barPositions?: Array<{ key: string; x: number; width: number }>;
  isStacked?: boolean;
}

export const isWithinSeriesBounds = (
  mouseY: number,
  seriesY: number,
  zeroLineY: number,
): boolean => {
  const minY = Math.min(seriesY, zeroLineY);
  const maxY = Math.max(seriesY, zeroLineY);
  return mouseY >= minY && mouseY <= maxY;
};

// Fallback detection for when no series boundaries contain the mouse

export const detectFallbackSeries = (
  validSeries: SeriesDetectionData[],
  mouseY: number,
  axes: { left: { scale: any } | null; right: { scale: any } | null },
): string | undefined => {
  if (validSeries.length === 0) {
    return undefined;
  }

  let closestSeries = validSeries[0];
  let minDistance = Infinity;

  // Process series in reverse order so that when multiple series have identical values,
  // we pick the one rendered last (on top in SVG)
  for (let i = validSeries.length - 1; i >= 0; i--) {
    const series = validSeries[i];
    const axis = axes[series.axis];
    if (!axis) {
      continue;
    }
    const seriesY = axis.scale(series.value ?? 0);
    const distance = Math.abs(mouseY - seriesY);

    if (distance < minDistance) {
      minDistance = distance;
      closestSeries = series;
    }
  }

  const FALLBACK_TOLERANCE = 3;
  return minDistance <= FALLBACK_TOLERANCE ? closestSeries.key : undefined;
};

export const detectHoveredSeries = (
  mouseY: number,
  seriesData: SeriesDetectionData[],
  axes: { left: { scale: any } | null; right: { scale: any } | null },
  mouseX?: number,
  barPositions?: Array<{ key: string; x: number; width: number }>,
  isStacked = false,
): string | undefined => {
  if (seriesData.length === 0) {
    return undefined;
  }
  const validSeries = seriesData.filter((s) => s.value !== null && (!isStacked || s.value > 0));
  if (validSeries.length === 0) {
    return undefined;
  }

  const config: SeriesDetectionConfig = {
    mouseX,
    mouseY,
    barPositions,
    isStacked,
  };

  const stackedResult = isStacked ? detectStackedSeries(config, validSeries, axes) : undefined;
  if (stackedResult !== undefined) {
    return stackedResult;
  }

  const groupedBarResult = detectGroupedBarSeries(config, validSeries, axes);
  if (groupedBarResult !== undefined) {
    if (groupedBarResult !== null) {
      return groupedBarResult;
    }

    const onlyBarSeries = validSeries.every((series) => series.chartType === 'bar');
    if (onlyBarSeries) {
      return undefined;
    }
  }

  const standardResult = detectStandardSeries(config, validSeries, axes);
  if (standardResult !== undefined) {
    return standardResult;
  }

  // 3. Fallback to closest series if nothing else worked
  return detectFallbackSeries(validSeries, mouseY, axes);
};
export const detectGroupedBarSeries = (
  config: SeriesDetectionConfig,
  validSeries: SeriesDetectionData[],
  axes: { left: { scale: any } | null; right: { scale: any } | null },
): string | undefined | null => {
  const { mouseX, mouseY, barPositions } = config;

  if (mouseX === undefined || !barPositions || barPositions.length === 0) {
    return undefined;
  }

  const barSeries = validSeries.filter((s) => s.chartType === 'bar');
  if (barSeries.length <= 1) {
    return undefined;
  }

  const zeroY = {
    left: axes.left?.scale(0) ?? 0,
    right: axes.right?.scale(0) ?? 0,
  };

  const hoveredBarPos = barPositions.find(
    (barPos) => mouseX >= barPos.x && mouseX <= barPos.x + barPos.width,
  );

  if (!hoveredBarPos) {
    return null;
  }

  const series = barSeries.find((s) => s.key === hoveredBarPos.key);
  if (!series) {
    return null;
  }

  const axis = axes[series.axis];
  if (!axis) {
    return null;
  }

  const seriesY = axis.scale(series.value ?? 0);
  const zeroLineY = zeroY[series.axis];

  if (isWithinSeriesBounds(mouseY, seriesY, zeroLineY)) {
    return series.key;
  }

  // Mouse is over a bar's X position but not within its Y bounds (e.g., above/below the bar)
  // Return null to prevent fallback detection from incorrectly selecting this bar
  return null;
};

export const detectStackedSeries = (
  config: SeriesDetectionConfig,
  validSeries: SeriesDetectionData[],
  axes: { left: { scale: any } | null; right: { scale: any } | null },
): string | undefined => {
  const { mouseY, mouseX, barPositions } = config;

  // Group by axis and calculate cumulative values for stacking
  const axesData: { left: SeriesDetectionData[]; right: SeriesDetectionData[] } = {
    left: [],
    right: [],
  };

  validSeries.forEach((series) => {
    axesData[series.axis].push(series);
  });

  // Check if we're hovering over bars - if so, check X position first
  const barSeries = validSeries.filter((s) => s.chartType === 'bar');
  const hasBarPositions = barPositions !== undefined && barPositions.length > 0;

  // For bar stacks, check X position first and return early if not over a bar
  if (barSeries.length > 0 && hasBarPositions && mouseX !== undefined) {
    // Find which bar we're hovering over (if any) based on X position
    // Note: All segments in a stack share the same X position
    const hoveredBar = barPositions.find(
      (barPos) => mouseX >= barPos.x && mouseX <= barPos.x + barPos.width,
    );

    // If not hovering over any bar X position, return undefined
    if (!hoveredBar) {
      return undefined;
    }
  }

  // Now check Y position to find which segment in the stack
  for (const axisKey of ['left', 'right'] as const) {
    const axisData = axesData[axisKey];
    if (axisData.length === 0) {
      continue;
    }

    const axis = axes[axisKey];
    if (!axis) {
      continue;
    }
    const valueScale = axis.scale;
    let cumulativeValue = 0;

    for (let i = axisData.length - 1; i >= 0; i--) {
      const series = axisData[i];
      const value = series.value ?? 0;

      const stackBottom = cumulativeValue;
      const stackTop = cumulativeValue + value;
      const isWithinSegment = mouseY >= valueScale(stackTop) && mouseY <= valueScale(stackBottom);

      if (isWithinSegment) {
        return series.key;
      }

      cumulativeValue += value;
    }
  }

  return undefined;
};

export const detectStandardSeries = (
  config: SeriesDetectionConfig,
  validSeries: SeriesDetectionData[],
  axes: { left: { scale: any } | null; right: { scale: any } | null },
): string | undefined => {
  const { mouseY, mouseX, barPositions } = config;

  const zeroY = {
    left: axes.left?.scale(0) ?? 0,
    right: axes.right?.scale(0) ?? 0,
  };

  // Find the series whose boundary the mouse is closest to
  let bestSeries = null;
  let bestDistance = Infinity;

  // Process series in reverse order so that when multiple series have identical values,
  // we pick the one rendered last (on top in SVG)
  for (let i = validSeries.length - 1; i >= 0; i--) {
    const series = validSeries[i];
    const axis = axes[series.axis];
    if (!axis) {
      continue;
    }
    const seriesY = axis.scale(series.value ?? 0);
    const zeroLineY = zeroY[series.axis];

    if (series.chartType === 'bar') {
      if (mouseX !== undefined && barPositions && barPositions.length > 0) {
        const barPos = barPositions.find((bp) => bp.key === series.key);

        // Skip this bar if not within X bounds
        if (barPos && !(mouseX >= barPos.x && mouseX <= barPos.x + barPos.width)) {
          continue;
        }
      }

      if (isWithinSeriesBounds(mouseY, seriesY, zeroLineY)) {
        const distanceToBoundary = Math.abs(mouseY - seriesY);

        if (distanceToBoundary <= bestDistance) {
          bestDistance = distanceToBoundary;
          bestSeries = series;
        }
      }
    } else if (series.chartType === 'area') {
      if (isWithinSeriesBounds(mouseY, seriesY, zeroLineY)) {
        const distanceToBoundary = Math.abs(mouseY - seriesY);

        if (distanceToBoundary <= bestDistance) {
          bestDistance = distanceToBoundary;
          bestSeries = series;
        }
      }
    } else if (series.chartType === 'line') {
      // For lines, use a tolerance and distance to the line
      const LINE_TOLERANCE = 3;
      const distanceToLine = Math.abs(mouseY - seriesY);

      if (distanceToLine <= LINE_TOLERANCE && distanceToLine < bestDistance) {
        bestDistance = distanceToLine;
        bestSeries = series;
      }
    }
  }

  return bestSeries?.key;
};
